import { useState, useEffect, useCallback, useRef } from 'react';
import { Brain, RefreshCw, CircleAlert as AlertCircle, Target, ChevronDown, ChevronUp, Package, Filter } from 'lucide-react';
import { logAccountActivity } from '../../lib/activityLog';
import { useRealtimeStatus } from '../../lib/useRealtimeStatus';
import { canInvokeEdgeFn } from '../../lib/edgeFnGuard';
import { Sentry } from '../../lib/sentry';
import { useApi } from '../../lib/useApi';
import type { Account, UseCaseRecord, UseCaseItem } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';
import FormattedText from '../common/FormattedText';
import UseCaseCard from './plan/UseCaseCard';
import UseCaseDetail from './plan/UseCaseDetail';
import WhyNowMap from './triggers/WhyNowMap';

interface Props {
  account: Account;
  onUseCaseChange?: (uc: UseCaseRecord) => void;
  isOwner?: boolean;
}

type UCStatus = 'pending' | 'processing' | 'completed' | 'error';

const STAGE_LABELS: Record<number, string> = {
  0: 'Preparing',
  1: 'Assembling inputs',
  2: 'Generating use cases',
  3: 'Processing output',
  4: 'Parsing structured data',
  5: 'Complete',
};

function getUseCaseProductNames(uc: UseCaseItem): string[] {
  if (uc.vendor_product_names && uc.vendor_product_names.length > 0) return uc.vendor_product_names;
  if (uc.vendor_product_name) return [uc.vendor_product_name];
  return [];
}

const OVERALL_TIMEOUT_MS = 25 * 60 * 1000;
const STAGE_STALL_MS = 10 * 60 * 1000;

export default function PlanTab({ account, onUseCaseChange, isOwner = true }: Props) {
  const { apiGet, apiPost } = useApi();
  const orgQuery = `org_id=${encodeURIComponent(account.org_id)}`;
  const [uc, setUc] = useState<UseCaseRecord | null>(null);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [expandedVerbose, setExpandedVerbose] = useState(false);
  const [productFilter, setProductFilter] = useState<string>('all');
  const [stale, setStale] = useState(false);
  const [lastKnownStage, setLastKnownStage] = useState<number>(-1);
  const [stageChangedAt, setStageChangedAt] = useState<number>(Date.now());

  const onUseCaseChangeRef = useRef(onUseCaseChange);
  onUseCaseChangeRef.current = onUseCaseChange;

  const status: UCStatus = (uc?.status as UCStatus) || 'pending';
  const processing = status === 'processing';
  const showLoading = (launching || processing) && !stale;

  const loadUC = useCallback(async () => {
    // use_cases is served by GET /v1/account/{id}/value (requireAccountAccess =
    // legacy `can_access_account` SELECT RLS); the record holds the exact column
    // set this tab consumes (mirrors the legacy `.maybeSingle()` → row or null).
    const { use_cases: data } = await apiGet<{ use_cases: UseCaseRecord | null }>(
      `/v1/account/${account.id}/value?${orgQuery}`,
    );
    if (data) {
      setUc(data);
      // Through the ref, so a caller passing an inline arrow does not change this
      // callback's identity and re-trigger the mount effect on every parent render.
      onUseCaseChangeRef.current?.(data);
    }
    setLoaded(true);
    return data;
  }, [apiGet, account.id, orgQuery]);

  useEffect(() => { loadUC(); }, [loadUC]);

  useEffect(() => {
    if (uc && typeof uc.current_stage === 'number' && uc.current_stage !== lastKnownStage) {
      setLastKnownStage(uc.current_stage);
      setStageChangedAt(Date.now());
    }
  }, [uc?.current_stage, lastKnownStage]);

  useEffect(() => {
    if (!processing || !uc?.updated_at) {
      setStale(false);
      return;
    }
    const check = () => {
      const overallElapsed = Date.now() - new Date(uc.updated_at).getTime();
      const stageElapsed = Date.now() - stageChangedAt;
      if (overallElapsed >= OVERALL_TIMEOUT_MS || stageElapsed >= STAGE_STALL_MS) {
        setStale(true);
      }
    };
    check();
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, [processing, uc?.updated_at, stageChangedAt]);

  const resetStaleRecord = async () => {
    if (!uc) return;
    setStale(false);
    // POST /v1/account/{id}/use-cases/reset (requireAccountOrgMember = legacy
    // `use_cases` UPDATE RLS `is_account_org_member`); marks the account's single
    // use-case row as errored so the user can retry, then re-reads it.
    await apiPost(`/v1/account/${account.id}/use-cases/reset`, { org_id: account.org_id });
    await loadUC();
  };

  useRealtimeStatus({
    table: 'use_cases',
    filterColumn: 'account_id',
    filterValue: account.id,
    enabled: (processing || launching) && !stale,
    statusField: 'status',
    terminalStatuses: ['completed', 'error'],
    fallbackIntervalMs: 4000,
    // Poll the use_cases row through the signed GET /v1/account/{id}/value read.
    fetcher: useCallback(async () => {
      const { use_cases: row } = await apiGet<{ use_cases: Record<string, unknown> | null }>(
        `/v1/account/${account.id}/value?${orgQuery}`,
      );
      return row;
    }, [apiGet, account.id, orgQuery]),
    onUpdate: useCallback((row: Record<string, unknown>) => {
      const data = row as unknown as UseCaseRecord;
      setUc(data);
      onUseCaseChangeRef.current?.(data);
      if (data.status === 'completed' || data.status === 'error') {
        setLaunching(false);
        setStale(false);
      }
    }, []),
  });

  const runGeneration = async () => {
    if (!canInvokeEdgeFn(`use-cases-${account.id}`)) return;
    setLaunching(true);
    setError('');
    setSelectedIndex(null);
    setStale(false);
    setLastKnownStage(-1);
    setStageChangedAt(Date.now());

    setUc((prev) => ({
      ...(prev || {
        id: '',
        account_id: account.id,
        overview: '',
        use_case_data: [],
        ranking_rationale: '',
        raw_verbose_output: '',
        error_message: '',
        current_stage: 0,
        created_at: '',
        updated_at: '',
      }),
      status: 'processing',
      current_stage: 0,
    } as UseCaseRecord));

    try {
      apiPost('/v1/generate-use-cases', { account_id: account.id })
        .catch((err: unknown) => { Sentry.captureException(err, { tags: { context: 'generate-use-cases' } }); });

      logAccountActivity(account.id, 'use_cases_started', `Started use case generation for ${account.company_name}`);
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'use_case_generation' } });
      setError(err instanceof Error ? err.message : 'Failed to start generation');
      setLaunching(false);
    }
  };

  const completed = status === 'completed';
  const hasError = status === 'error';
  const useCases = uc?.use_case_data || [];
  const sortedUseCases = [...useCases].sort((a, b) => a.rank - b.rank);

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-app-ink">Use Cases</h2>
          <p className="text-sm text-app-muted mt-0.5">
            Enterprise-grade use cases for {account.company_name}
          </p>
        </div>
        {isOwner && (
        <button
          onClick={runGeneration}
          disabled={showLoading}
          className="btn-primary !py-2.5 !px-4 !text-sm"
        >
          {showLoading
            ? <><LoadingSpinner size="sm" /><span className="ml-2">Generating...</span></>
            : <><RefreshCw className="w-4 h-4 mr-2" />{uc && completed ? 'Regenerate Use Cases' : 'Generate Use Cases'}</>
          }
        </button>
        )}
      </div>

      {!isOwner && (
        <div className="flex items-center gap-3 p-4 bg-app-focus/10 border border-app-focus rounded-lg">
          <svg className="w-5 h-5 text-app-focus flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-sm text-app-focus">View only. Use case generation is managed by the account owner.</p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 p-4 bg-app-critical/10 border border-app-critical rounded-lg">
          <AlertCircle className="w-5 h-5 text-app-critical flex-shrink-0" />
          <p className="text-sm text-app-critical">{error}</p>
        </div>
      )}

      {isOwner && stale && (
        <div className="bg-app-warning/10 border border-app-warning rounded-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-app-warning mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-app-warning mb-2">Processing Appears Stuck</h3>
          <p className="text-sm text-app-warning mb-4">
            Generation has not made progress for an extended period. This usually means an upstream timeout occurred.
          </p>
          <button onClick={resetStaleRecord} className="btn-primary">
            <RefreshCw className="w-4 h-4 mr-2" /> Reset &amp; Retry
          </button>
        </div>
      )}

      {showLoading && (
        <UCProgressIndicator currentStage={uc?.current_stage || 0} />
      )}

      {!showLoading && completed && uc && (
        <>
          {uc.overview && (
            <div className="relative overflow-hidden rounded-lg border border-forest-200 bg-app-surface shadow-none">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-app-accent" />
              <div className="p-6 pl-8">
                <h3 className="text-sm font-bold text-forest-800 uppercase tracking-wider mb-3">Use Case Overview</h3>
                <FormattedText text={uc.overview} className="text-sm text-app-ink" />
              </div>
            </div>
          )}

          {sortedUseCases.length > 0 && (
            <div className="space-y-4">
              <ProductFilterBar
                useCases={sortedUseCases}
                activeFilter={productFilter}
                onFilterChange={setProductFilter}
              />
              {(productFilter === 'all'
                ? sortedUseCases
                : sortedUseCases.filter(uc => {
                    const names = getUseCaseProductNames(uc);
                    if (productFilter === 'untagged') return names.length === 0;
                    return names.includes(productFilter);
                  })
              ).map((useCase) => {
                const originalIndex = sortedUseCases.indexOf(useCase);
                return (
                  <UseCaseCard
                    key={originalIndex}
                    useCase={useCase}
                    onClick={() => setSelectedIndex(originalIndex)}
                  />
                );
              })}
            </div>
          )}

          {uc.ranking_rationale && (
            <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
              <h3 className="text-sm font-bold text-app-muted uppercase tracking-wider mb-3">Ranking Rationale</h3>
              <FormattedText text={uc.ranking_rationale} className="text-sm text-app-ink" />
            </div>
          )}

          <WhyNowMap account={account} useCases={sortedUseCases} />

          {uc.raw_verbose_output && (
            <div className="bg-app-surface rounded-lg border border-app-line shadow-none">
              <button
                onClick={() => setExpandedVerbose(!expandedVerbose)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-app-canvas transition-colors rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-app-muted" />
                  <span className="text-sm font-semibold text-app-ink">Full Use Case Research Output</span>
                </div>
                {expandedVerbose
                  ? <ChevronUp className="w-4 h-4 text-app-muted" />
                  : <ChevronDown className="w-4 h-4 text-app-muted" />
                }
              </button>
              {expandedVerbose && (
                <div className="px-5 pb-5 border-t border-app-line">
                  <div className="mt-4 max-w-none text-app-muted text-xs">
                    <FormattedText text={uc.raw_verbose_output} />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!showLoading && hasError && (
        <div className="bg-app-critical/10 border border-app-critical rounded-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-app-critical mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-app-critical mb-2">Generation Failed</h3>
          <p className="text-sm text-app-critical mb-4">
            {uc?.error_message || 'Something went wrong during use case generation. Please try again.'}
          </p>
          <button onClick={runGeneration} disabled={launching} className="btn-primary">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </button>
        </div>
      )}

      {!uc && !showLoading && (
        <div className="bg-app-canvas border border-app-line rounded-lg p-12 text-center">
          <Target className="w-12 h-12 text-app-line-strong mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-app-ink mb-2">No Use Cases Yet</h3>
          <p className="text-sm text-app-muted mb-6 max-w-md mx-auto">
            Generate enterprise-grade use cases using the value pyramid, value statements, and vendor intelligence.
          </p>
        </div>
      )}

      {selectedIndex !== null && sortedUseCases[selectedIndex] && (
        <UseCaseDetail
          useCase={sortedUseCases[selectedIndex]}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </div>
  );
}

function UCProgressIndicator({ currentStage }: { currentStage: number }) {
  const stages = [
    { num: 1, label: 'Inputs' },
    { num: 2, label: 'Generating' },
    { num: 3, label: 'Processing' },
    { num: 4, label: 'Parsing' },
    { num: 5, label: 'Complete' },
  ];

  return (
    <div className="rounded-lg border border-app-line bg-app-surface p-5 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Brain className="w-6 h-6 text-forest-600 animate-pulse" />
        <div>
          <h3 className="text-base font-semibold text-app-ink">Generating use cases</h3>
          <p className="text-sm text-app-muted">{STAGE_LABELS[currentStage] || 'Processing...'} This can take 15 to 20 minutes for complex accounts. You can leave this page while it runs.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {stages.map((stage) => {
          const isComplete = currentStage > stage.num;
          const isCurrent = currentStage === stage.num;
          return (
            <div key={stage.num} className="flex-1">
              <div className="h-2.5 rounded-full overflow-hidden bg-app-line" role="progressbar" aria-label={stage.label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={isComplete ? 100 : isCurrent ? 50 : 0}>
                <div
                  className={`h-full rounded-full transition duration-700 ${
                    isComplete ? 'bg-app-positive' :
                    isCurrent ? 'bg-app-accent animate-pulse' :
                    ''
                  }`}
                  style={{ width: isComplete ? '100%' : isCurrent ? '50%' : '0%' }}
                />
              </div>
              <p className={`text-xs mt-2 text-center ${
                isComplete ? 'text-app-positive font-medium' :
                isCurrent ? 'text-app-ink font-medium' :
                'text-app-muted'
              }`}>
                {stage.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductFilterBar({ useCases, activeFilter, onFilterChange }: {
  useCases: UseCaseItem[];
  activeFilter: string;
  onFilterChange: (filter: string) => void;
}) {
  const allNames = useCases.flatMap(uc => getUseCaseProductNames(uc));
  const uniqueProducts = [...new Set(allNames)];

  if (uniqueProducts.length === 0) {
    return (
      <h3 className="text-sm font-bold text-app-muted uppercase tracking-wider">
        Ranked Use Cases ({useCases.length})
      </h3>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <h3 className="text-sm font-bold text-app-muted uppercase tracking-wider">
        Ranked Use Cases ({useCases.length})
      </h3>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-app-muted mr-1" />
        <button
          onClick={() => onFilterChange('all')}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            activeFilter === 'all'
              ? 'bg-forest-100 text-forest-800 border border-forest-200'
              : 'bg-app-canvas text-app-muted border border-transparent hover:bg-app-line'
          }`}
        >
          All
        </button>
        {uniqueProducts.map(product => (
          <button
            key={product}
            onClick={() => onFilterChange(product)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              activeFilter === product
                ? 'bg-app-focus/10 text-app-focus border border-app-focus/30'
                : 'bg-app-canvas text-app-muted border border-transparent hover:bg-app-line'
            }`}
          >
            <Package className="w-3 h-3" />
            {product}
          </button>
        ))}
      </div>
    </div>
  );
}
